/**
 * What the desk's Browse row says, decided in a file with no imports — the
 * contract `browse.ts`, `night.ts` and `rowMeta.ts` keep, so the Worker's
 * suite loads it directly (`test/deskBrowse.test.ts`).
 *
 * **Nothing here writes a sentence.** Every string returned is a name, a
 * number or a clock a page printed, or one of the fixed words below.
 * A description is `summary ?? film.overview` and is the caller's: this file
 * only clips it.
 *
 * The imports are `rankBase.ts` and `zone.ts`, each dependency-free and named with its
 * extension: what a null score is worth, which the two sorts below share with
 * every other ranked list, and the zones a row's times and "today" are read in.
 */
import { scoreOf } from './rankBase.ts'
import { clockIn, dayIn, hereZone, zoneOfCity } from './zone.ts'
import { currencyOfCity, priceSymbol } from './currency.ts'

// ---------------------------------------------------------------------------
// The shapes read, declared rather than imported (`browse.ts`'s own reason)
// ---------------------------------------------------------------------------

export interface DeskRow {
  id: string
  title: string
  type: string
  discipline?: string | null
  title_original?: string | null
  run_start?: string | null
  run_end?: string | null
  published_on?: string | null
  next_showing?: string | null
  /** C2.1: the town, which names the zone the row's times are read in. */
  city?: string | null
  next_opening?: string | null
  score?: number | null
  duration_min?: number | null
  venue?: string | null
  district?: string | null
  is_free?: boolean | null
  price_min?: number | null
  price_max?: number | null
  price_note?: string | null
  english_access?: string | null
  ticket_status?: string | null
  performers?: Record<string, string> | null
  classical?: {
    ensemble?: { name: string } | null
    conductor?: string | null
    soloists?: { name: string; instrument?: string | null }[]
    programme?: { composer?: string | null; work?: string | null; work_src?: string }[]
    duration_min?: number | null
  } | null
  stage?: {
    company?: string | null; conductor?: string | null; director?: string | null
    composer?: string | null; work?: string | null; author?: string | null
  } | null
  film?: {
    year?: number; directors?: string[]; countries?: string[]; genres?: string[]
    original_title?: string; runtime_min?: number; overview?: string
    rt_score?: number | null; imdb_rating?: number | null
  } | null
  night?: {
    lineup?: { name: string }[]; doors?: string; floors?: string[]; genre_tags?: string[]
  } | null
  exhibition?: { artists?: string[] } | null
}

export interface DeskShowing {
  starts_at: string
  venue?: string | null
  kind?: string | null
}

/** The row shapes `detailsOf` tells apart. `browse.ts`'s `kindOf` decides the
 *  kind; this is what the kind is drawn as. */
export type Shape = 'film' | 'programme' | 'night' | 'exhibition' | 'other'

/** A kind from the row alone, for a caller with no `browse.ts` to ask: the
 *  same cut `kindOf` makes on the fields this file declares. */
export function inferKind(row: DeskRow): string {
  if (row.discipline === 'film' || row.type === 'screening') return 'film'
  if (row.type === 'exhibition') return 'exhibition'
  if (row.night) return 'club'
  if (row.type === 'concert') return 'concert'
  if (row.type === 'performance') return row.discipline === 'opera' || row.discipline === 'dance' ? 'opera' : 'theatre'
  return 'talk'
}

/** Fixed words for a kind, as the right-hand column and the pane print it. */
export const KIND_WORD: Record<string, string> = {
  concert: 'Concert', opera: 'Opera and dance', theatre: 'Theatre', exhibition: 'Exhibition', film: 'Film',
  club: 'Club', gig: 'Gig', quiz: 'Quiz', talk: 'Talk', market: 'Market',
}

export const shapeOf = (kind: string): Shape =>
  kind === 'film' ? 'film'
    : kind === 'concert' || kind === 'opera' || kind === 'theatre' ? 'programme'
      : kind === 'club' || kind === 'gig' || kind === 'quiz' ? 'night'
        : kind === 'exhibition' ? 'exhibition'
          : 'other'

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const D = (s: string) => new Date(`${s}T12:00:00Z`)
export const addDays = (s: string, n: number): string =>
  new Date(D(s).getTime() + n * 86400000).toISOString().slice(0, 10)

/** The current region's calendar day of a moment — "today" and its kin
 *  (C2.1; the name is older than regions). A row's own timestamp is read in
 *  the row's zone instead: `rowDay`. */
export const berlinDay = (at: string): string => dayIn(at, hereZone())

/** The day a row's own timestamp falls on, in its town's region's zone. */
const rowDay = (row: DeskRow, at: string): string => dayIn(at, zoneOfCity(row.city))

/** `18:00`, the wall clock in `tz` — the row's zone, the time printed on the
 *  ticket. */
export const clockOf = (at: string, tz: string): string => clockIn(at, tz)

const short = (day: string, o: Intl.DateTimeFormatOptions) =>
  D(day).toLocaleDateString('en-GB', { timeZone: 'UTC', ...o })

/** `Fri 2`. */
const dayShort = (day: string) => short(day, { weekday: 'short', day: 'numeric' })

/** A bare midnight is a date with no time on it, as the phone reads it
 *  (`parseTime` drops it): an opening dated by day arrives as local midnight
 *  in the row's zone, `tz`. */
export const clockText = (at: string, tz: string): string => {
  const c = clockOf(at, tz)
  return c === '00:00' ? '' : c
}

/** The clock and day a row occurs on, or nothing. `next_showing` and
 *  `next_opening` are instants, read in the row's zone; a run or a published
 *  date is a day only. */
export function startOf(row: DeskRow): { day: string; clock: string | null } | null {
  const at = row.next_showing ?? row.next_opening
  if (at) return { day: rowDay(row, at), clock: clockText(at, zoneOfCity(row.city)) || null }
  const day = row.run_start ?? row.published_on
  return day ? { day, clock: null } : null
}

/** An exhibition with a run of its own sits under the days, not in them. */
export const isRunning = (row: DeskRow, kind: string): boolean =>
  kind === 'exhibition' && !!row.run_start && !!row.run_end && row.run_end !== row.run_start

/** `browse.ts`'s `STARTED_GRACE_MIN`, which this file cannot import. */
export const STARTED_GRACE_MIN = 30

/**
 * Has a row's own start, today, passed by more than the grace? The rule
 * `inWindow` applies to a one-day row, applied to the list as well so a day's
 * heading and a window's count are made of the same rows: the list kept
 * VATERLAND at 16:10 at 16:41 while the Tonight window had dropped it
 * (heading 108, filter 106). A running exhibition is still going, whatever its
 * start said, and is never "started".
 */
export function hasStarted(row: DeskRow, kind: string, now: Date, graceMin = STARTED_GRACE_MIN): boolean {
  if (isRunning(row, kind)) return false
  const at = row.next_showing ?? row.next_opening
  if (!at || rowDay(row, at) !== berlinDay(now.toISOString())) return false
  return now.getTime() - new Date(at).getTime() > graceMin * 60_000
}

/** Does a row fall inside the next `n` days — the one window `browse.ts` has
 *  no word for (Next 30 days). A run counts by overlap, as there. */
export function withinDays(row: DeskRow, today: string, n: number): boolean {
  const to = addDays(today, n)
  if (row.run_start && row.run_end && row.run_end !== row.run_start) return row.run_start <= to && row.run_end >= today
  const s = startOf(row)
  return !!s && s.day >= today && s.day <= to
}

// ---------------------------------------------------------------------------
// Descriptions
// ---------------------------------------------------------------------------

/** At most `max` characters, cut at a word and ended with an ellipsis. The
 *  CSS clamp is three lines; this keeps the DOM from holding a page. */
export function clipDescription(text: string | null | undefined, max = 320): string | null {
  const t = (text ?? '').replace(/\s+/g, ' ').trim()
  if (!t) return null
  if (t.length <= max) return t
  const cut = t.slice(0, max)
  const at = cut.lastIndexOf(' ')
  return `${(at > max * 0.6 ? cut.slice(0, at) : cut).replace(/[\s,;:.\-–—]+$/, '')}…`
}

// ---------------------------------------------------------------------------
// Who and facts, by kind
// ---------------------------------------------------------------------------

export interface Details {
  shape: Shape
  /** The line under the title. For a night it is the lineup, which the row
   *  sets in weight 500; for the rest it is plain. */
  who: string | null
  /** Smaller grey line: genres, length, ratings; doors, floors; the run. */
  facts: string | null
  /** A concert's works, composer first. */
  programme: { composer: string | null; work: string }[]
}

const has = (title: string, name: string) => title.toLowerCase().includes(name.trim().toLowerCase())

/** Names the title has not already said, in order, once each. */
export function newNames(names: (string | null | undefined)[], title: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const n of names) {
    const v = (n ?? '').trim()
    if (!v || has(title, v) || seen.has(v.toLowerCase())) continue
    seen.add(v.toLowerCase())
    out.push(v)
  }
  return out
}

const LINEUP_SHOWN = 8

const uniq = (xs: (string | null | undefined)[]): string[] => {
  const seen = new Set<string>()
  return xs.filter((x): x is string => !!x && !seen.has(x.toLowerCase()) && !!seen.add(x.toLowerCase()))
}

const minutes = (row: DeskRow): number | null =>
  row.film?.runtime_min ?? row.classical?.duration_min ?? row.duration_min ?? null

export const lengthOf = minutes

/** `Doors 23:00`, `3 floors`, genres — the three things a club row decides on. */
function nightFacts(row: DeskRow): string | null {
  const n = row.night
  const floors = n?.floors?.length ?? 0
  return [
    uniq(n?.genre_tags ?? []).join(', '),
    n?.doors ? `Doors ${n.doors}` : '',
    floors > 1 ? `${floors} floors` : '',
  ].filter(Boolean).join(' · ') || null
}

export function detailsOf(row: DeskRow, kind: string = inferKind(row)): Details {
  const shape = shapeOf(kind)
  const title = row.title
  const p = row.performers ?? {}

  if (shape === 'film') {
    const f = row.film ?? {}
    const directors = f.directors?.length ? f.directors : (p.director ? [p.director] : [])
    const who = [directors.join(', '), f.year ? String(f.year) : '', (f.countries ?? []).join(', ')].filter(Boolean).join(' · ')
    const mins = minutes(row)
    const facts = [
      (f.genres ?? []).join(', '),
      mins ? `${mins} min` : '',
      typeof f.imdb_rating === 'number' ? `IMDb ${f.imdb_rating.toFixed(1)}` : '',
      typeof f.rt_score === 'number' ? `Rotten Tomatoes ${f.rt_score}%` : '',
    ].filter(Boolean).join(' · ')
    return { shape, who: who || null, facts: facts || null, programme: [] }
  }

  if (shape === 'programme') {
    const c = row.classical
    const s = row.stage
    let names: (string | null | undefined)[]
    if (c) {
      names = [c.ensemble?.name, c.conductor, ...(c.soloists ?? []).map(x => x.name)]
    } else if (s) {
      names = [s.company, s.conductor, s.director]
    } else {
      names = [p.ensemble, p.conductor, p.soloists, p.director, p.cast]
    }
    // Soloists carry their instrument after the name; list it back on.
    const withInstrument = (n: string) => {
      const solo = c?.soloists?.find(x => x.name === n)
      return solo?.instrument ? `${n}, ${solo.instrument}` : n
    }
    const who = newNames(names, title).map(withInstrument).join(' · ')
    const listed = c?.programme?.length
      ? c.programme.map(i => ({ composer: i.composer ?? null, work: i.work ?? i.work_src ?? '' })).filter(i => i.work)
      : (p.programme ?? '').split(/\s*;\s*/).map(w => w.trim()).filter(Boolean).map(work => ({ composer: null, work }))
    // A piece listed twice (a house printing both halves of one work) is one line.
    const seen = new Set<string>()
    const programme = listed.filter(i => { const k = `${i.composer}|${i.work}`.toLowerCase(); return !seen.has(k) && !!seen.add(k) })
    const mins = minutes(row)
    return { shape, who: who || null, facts: mins ? `${mins} min` : null, programme }
  }

  if (shape === 'night') {
    const names = uniq((row.night?.lineup ?? []).map(l => l.name))
    const shown = names.slice(0, LINEUP_SHOWN).join(', ')
    const who = shown ? (names.length > LINEUP_SHOWN ? `${shown} + ${names.length - LINEUP_SHOWN} more` : shown) : null
    return { shape, who, facts: nightFacts(row), programme: [] }
  }

  if (shape === 'exhibition') {
    const who = newNames(row.exhibition?.artists ?? [], title).join(', ')
    return { shape, who: who || null, facts: runLine(row), programme: [] }
  }

  const who = newNames([p.director && `dir. ${p.director}`, p.cast, p.ensemble], title).join(', ')
  return { shape, who: who || null, facts: null, programme: [] }
}

/** `4 Oct – 15 Nov 2026`, where both ends are known. */
export function runLine(row: DeskRow): string | null {
  if (!row.run_start || !row.run_end) return null
  if (row.run_end === row.run_start) return short(row.run_start, { day: 'numeric', month: 'short', year: 'numeric' })
  return `${short(row.run_start, { day: 'numeric', month: 'short' })} – ${short(row.run_end, { day: 'numeric', month: 'short', year: 'numeric' })}`
}

/** The time column: a clock for a showing, `until 4 Oct` for a run. */
export function timeOf(row: DeskRow, kind: string): string {
  if (isRunning(row, kind)) return `until ${short(row.run_end!, { day: 'numeric', month: 'short' })}`
  return startOf(row)?.clock ?? '—'
}

// ---------------------------------------------------------------------------
// Also
// ---------------------------------------------------------------------------

/** The other showings within a fortnight, after the one this row stands on:
 *  `Fri 2 18:00 · Sat 3 17:00`, a venue appended where it is not the row's. */
export function alsoOf(row: DeskRow, showings: DeskShowing[], today: string): string | null {
  const own = startOf(row)
  const tz = zoneOfCity(row.city)
  const limit = addDays(today, 14)
  const seen = new Set<string>()
  const out: string[] = []
  const sorted = showings
    .filter(s => s.kind !== 'run' && s.kind !== 'annotation')
    .sort((a, b) => (a.starts_at < b.starts_at ? -1 : 1))
  for (const s of sorted) {
    const day = rowDay(row, s.starts_at)
    if (day < today || day > limit) continue
    if (own && (day < own.day || (day === own.day && own.clock !== null && clockOf(s.starts_at, tz) <= own.clock))) continue
    if (own && !own.clock && day <= own.day) continue
    // The instant *and* the place: a film at ten cinemas (cards WP4) can start
    // at one minute in two of them, and keyed on the minute alone the second
    // cinema's night vanished. The same night listed twice still collapses.
    const key = `${s.starts_at.slice(0, 16)}|${s.venue ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(`${[dayShort(day), clockText(s.starts_at, tz)].filter(Boolean).join(' ')}${s.venue && s.venue !== row.venue ? ` ${s.venue}` : ''}`)
  }
  if (!out.length) return null
  return out.length > 4 ? `${out.slice(0, 4).join(' · ')} · +${out.length - 4}` : out.join(' · ')
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

export interface DayGroup<T> { day: string; rows: T[] }

/**
 * Rows under their days, soonest day first, each day by time then score; and
 * the running exhibitions apart, by closing date. The caller has already
 * windowed. A row stating no date at all sits under today rather than going
 * missing.
 */
export function groupByDay<T extends DeskRow>(
  rows: T[], today: string, kindOf: (r: T) => string = inferKind,
): { days: DayGroup<T>[]; exhibitions: T[] } {
  const exhibitions = rows.filter(r => isRunning(r, kindOf(r)))
    .sort((a, b) => (a.run_end! < b.run_end! ? -1 : a.run_end! > b.run_end! ? 1 : scoreOf(b) - scoreOf(a)))
  const buckets = new Map<string, T[]>()
  for (const r of rows) {
    if (isRunning(r, kindOf(r))) continue
    const day = startOf(r)?.day ?? today
    const list = buckets.get(day)
    if (list) list.push(r); else buckets.set(day, [r])
  }
  const key = (r: T) => `${startOf(r)?.clock ?? '99:99'}`
  const days = [...buckets.keys()].sort().map(day => ({
    day,
    rows: buckets.get(day)!.sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : scoreOf(b) - scoreOf(a))),
  }))
  return { days, exhibitions }
}

/** Rows split the way the list splits them: the ones that sit under a day, and
 *  the running exhibitions that sit under *Exhibitions on now*. A day's
 *  heading and the filter's count for that day both read this, so they agree. */
export function tally<T extends DeskRow>(rows: T[], kindOf: (r: T) => string = inferKind): { dated: number; running: number } {
  let running = 0
  for (const r of rows) if (isRunning(r, kindOf(r))) running++
  return { dated: rows.length - running, running }
}

/** A day heading: `Tonight` or `Tomorrow` with the long date beside it, else
 *  the long date alone. */
export function dayHeading(day: string, today: string): { name: string; long: string | null } {
  const long = short(day, { weekday: 'long', day: 'numeric', month: 'long' })
  if (day === today) return { name: 'Tonight', long }
  if (day === addDays(today, 1)) return { name: 'Tomorrow', long }
  return { name: long, long: null }
}

// ---------------------------------------------------------------------------
// The pane's facts table
// ---------------------------------------------------------------------------

const ACCESS: Record<string, string> = {
  native: 'English', surtitles: 'English surtitles', subtitles: 'English subtitles',
  none: 'German only', unknown: '',
}

export const languageOf = (row: DeskRow): string => ACCESS[row.english_access ?? 'unknown'] ?? ''

export const priceOf = (row: DeskRow): string => {
  if (row.is_free) return 'Free'
  // UI-audit a1-10: a zero on a row that is not `is_free` is a placeholder —
  // `rowMeta.ts`'s `placeholderPrice`, restated for this file's no-imports
  // contract. Its `price_note` is the raw schema.org fragment, so nothing.
  if (row.price_min === 0 && (row.price_max == null || row.price_max === 0)) return ''
  const sym = priceSymbol(currencyOfCity(row.city))
  if (row.price_min != null && row.price_max != null && row.price_min !== row.price_max) return `${sym}${row.price_min}–${row.price_max}`
  if (row.price_min != null) return `${sym}${row.price_min}`
  return row.price_note ?? ''
}

/** A card that stands for several places — a film at eleven cinemas — as the
 *  list's row says it: `where` is `setPlace`'s (`11 cinemas`), `when` is
 *  `runCount`'s (`37 showings to 7 Oct`). The caller computes both, as the
 *  row does; this file has no imports to compute them with. */
export interface SetFacts { where: string; when: string | null }

/** Label and value, empty values dropped.
 *
 *  UI-audit a3-07: a set's `venue`, `district` and `ticket_status` are one
 *  member's — VATERLAND's row is filed at Il Kino — and its `next_showing` is
 *  the set's, so the pane said "When 14:00, Where Il Kino" for a 14:00 at
 *  Delphi LUX. Given `set`, When and Where say what the list row says, and
 *  the one box office's ticket status goes: the showings list names each
 *  night's own place. */
export function factsTable(row: DeskRow, kind: string = inferKind(row), set: SetFacts | null = null): [string, string][] {
  const d = detailsOf(row, kind)
  const s = startOf(row)
  const when = set
    ? set.when ?? ''
    : isRunning(row, kind)
    ? runLine(row) ?? ''
    : s ? `${short(s.day, { weekday: 'long', day: 'numeric', month: 'long' })}${s.clock ? ` · ${s.clock}` : ''}` : ''
  const orig = row.film?.original_title ?? row.title_original ?? ''
  const mins = minutes(row)
  const f = row.film ?? {}
  return ([
    ['When', when],
    ['Where', set ? set.where : [row.venue, row.district].filter(Boolean).join(', ')],
    ['Who', d.who ?? ''],
    ['Original title', orig && orig !== row.title ? orig : ''],
    ['Kind', KIND_WORD[kind] ?? kind],
    ['Price', priceOf(row)],
    ['Language', languageOf(row)],
    ['Length', mins ? `${mins} min` : ''],
    ['IMDb', typeof f.imdb_rating === 'number' ? `${f.imdb_rating.toFixed(1)} / 10` : ''],
    ['Rotten Tomatoes', typeof f.rt_score === 'number' ? `${f.rt_score}%` : ''],
    ['Tickets', !set && row.ticket_status && row.ticket_status !== 'unknown' ? row.ticket_status.replace(/_/g, ' ') : ''],
    ['Genre', shape(d) === 'night' ? uniq(row.night?.genre_tags ?? []).join(', ') : (f.genres ?? []).join(', ')],
    ['Doors', row.night?.doors ?? ''],
  ] as [string, string][]).filter(([, v]) => v)
}

const shape = (d: Details) => d.shape

// ---------------------------------------------------------------------------
// The open row and the arrow keys
// ---------------------------------------------------------------------------

/**
 * The open row, if the list still draws it (UI-audit a3-06).
 *
 * `selected` was two things: the row the pane shows and the place the arrow
 * keys step from. A filter that took the row out of the list left the first
 * job done — the pane stayed on "Vier zurück" over a list of clubs — and the
 * second undone, so ArrowDown stepped from index -1 to the top. The pane
 * closes instead: a row the list no longer has has no position to step from
 * (where would a play sit in a list of clubs?), and closing is what Escape
 * already does. The caller clears its state when this answers null, so
 * widening the filter again does not reopen a pane nobody asked for.
 */
export function selectionIn(order: readonly string[], selected: string | null): string | null {
  return selected && order.includes(selected) ? selected : null
}

/** The row an arrow key moves to: the first row from no selection, else one
 *  step, stopping at either end. Null for an empty list. */
export function stepSelection(order: readonly string[], selected: string | null, dir: 1 | -1): string | null {
  if (!order.length) return null
  const at = selected ? order.indexOf(selected) : -1
  if (at < 0) return order[0]
  return order[Math.max(0, Math.min(order.length - 1, at + dir))]
}
